import {
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  getSessionDateLabel,
  getSessionTimeLabel,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import ButtonWidget from './widgets/button-widget';
import FooterWidget from './widgets/footer-widget';
import { templateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import { BlockRenderer } from './widgets/block-renderer';
import StyledBlockBox from './widgets/styled-block-box';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';

const DEFAULTS = templateStaticDefaults.carlton;

const CARLTON_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.badge]: {
    fitContent: true,
    align: 'left',
    color: 'var(--accent)',
    fontFamily: 'var(--body-font)',
    fontSize: '10px',
    fontWeight: 800,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    lineHeight: '1',
    background: '#FFFFFF',
    border: '1px',
    borderColor: 'var(--accent)',
    borderRadius: '4px',
    padding: '8px 12px',
    height: 86,
    margin: '0px 56px',
    blockBackground: 'var(--accent)', // matches title's backdrop so the pill sits on a continuous band
    blockBoxShadow: '0 -1px 0 0 var(--accent)', // covers the seam if reordered below title
  },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '48px',
    fontWeight: 600,
    lineHeight: '53px',
    background: 'var(--accent)',
    boxShadow: '0 -1px 0 0 var(--accent)', // covers the seam with badge above it
    padding: '20px 56px 56px 56px',
    margin: '0',
  },
  [BLOCK_ID.mainImage]: { width: 600, height: 320 },
  [BLOCK_ID.salutation]: {
    color: '#111111',
    fontFamily: 'var(--body-font)',
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '29px',
    padding: '32px 56px 12px 56px',
  },
  [BLOCK_ID.description]: {
    color: '#666666',
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '26px',
    padding: '4px 56px 32px 56px',
  },
  [BLOCK_ID.cta]: {
    size: 'large',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    border: '2px',
    borderColor: 'var(--accent)',
    fontWeight: 700,
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
  },
  [BLOCK_ID.tagline]: {
    color: 'var(--accent)',
    fontFamily: 'var(--heading-font)',
    fontSize: '18px',
    fontWeight: 700,
    // legacy wraps tagline+communityText together in one shared td (padding: '48px 64px') -
    // since each is its own block here, the wrapper's inset is distributed across both: top
    // goes on tagline, the 20px gap between them stays on tagline's own bottom padding
    padding: '48px 64px 20px 64px',
    background: 'var(--accent-soft)',
  },
  [BLOCK_ID.communityText]: {
    color: '#8DA08E',
    fontFamily: 'var(--body-font)',
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '17px',
    padding: '0 64px 48px 64px',
    background: 'var(--accent-soft)',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

const INFO_CARD_BLOCK_ID = BLOCK_ID.infoCard;

export const Carlton = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const secondaryBg = resolveThemedColor(
    customizations?.secondaryBackgroundColor,
    themeColors?.secondaryBackgroundColor,
    DEFAULTS.secondaryBackgroundColor
  );
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '8px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const sessionEndTimestamp = event.sessions[0]?.end_timestamp;
  const durationMinutes =
    sessionTimestamp && sessionEndTimestamp ? Math.round((sessionEndTimestamp - sessionTimestamp) / 60000) : null;
  const anchorHref = href || '#';
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Playfair Display", Georgia, serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    'Manrope, Arial, sans-serif'
  );
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription = description || customizations?.description || DEFAULTS.descriptionTemplate?.(event.name);
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  // per the Figma, the footer's text takes the communityText color
  const footerTextColor = CARLTON_BLOCK_PRESETS[BLOCK_ID.communityText].color as string;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryBg,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: CARLTON_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Carlton"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--accent-soft': secondaryBg,
            '--button-text': buttonText,
            '--heading': heading,
            '--body': body,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td style={{ background: 'var(--container)' }}>
                      {blocks.map((block) => {
                        if (block.id === INFO_CARD_BLOCK_ID) {
                          return (
                            <BlockShell
                              key={block.id}
                              block={block}
                              isEditMode={!!isEditMode}
                              isFirst={block.id === blocks[0]?.id}
                              isLast={block.id === blocks[blocks.length - 1]?.id}
                              selected={block.id === selectedBlockId}
                              onSelect={onSelectBlock}
                              onDelete={onDeleteBlock}
                              onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                              onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                            >
                              <StyledBlockBox
                                blockId={block.id}
                                style={block.style}
                                // the rows' own 10px adds to this, matching the card's original 24px edges
                                defaults={{
                                  margin: '12px 56px',
                                  padding: '14px 24px',
                                  background: secondaryBg,
                                  borderRadius: '12px',
                                }}
                              >
                                <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                                  <tbody>
                                    {durationMinutes !== null && (
                                      <tr>
                                        <td style={{ padding: '10px 0' }}>
                                          <span style={{ color: 'var(--accent)', marginRight: '12px' }}>●</span>
                                          <span
                                            style={{
                                              color: 'var(--accent)',
                                              fontSize: '15px',
                                              fontFamily: 'var(--body-font)',
                                            }}
                                          >
                                            {DEFAULTS.watchTimeLabel} ~{durationMinutes} min
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td style={{ padding: '10px 0' }}>
                                        <span style={{ color: 'var(--accent)', marginRight: '12px' }}>●</span>
                                        <span
                                          style={{
                                            color: 'var(--accent)',
                                            fontSize: '15px',
                                            fontFamily: 'var(--body-font)',
                                          }}
                                        >
                                          Available Until Dec 2026
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ padding: '10px 0' }}>
                                        <span style={{ color: 'var(--accent)', marginRight: '12px' }}>●</span>
                                        <span
                                          style={{
                                            color: 'var(--accent)',
                                            fontSize: '15px',
                                            fontFamily: 'var(--body-font)',
                                          }}
                                        >
                                          {DEFAULTS.availableOnDemandText}
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </StyledBlockBox>
                            </BlockShell>
                          );
                        }

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={CARLTON_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Carlton"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--accent-soft': secondaryBg,
          '--button-text': buttonText,
          '--heading': heading,
          '--body': body,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                <tr>
                  <td style={{ background: 'var(--accent)' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td valign="middle" style={{ padding: '32px 56px', height: 86 }}>
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      background: '#FFFFFF',
                                      border: '1px solid var(--button-text)',
                                      borderRadius: '4px',
                                      padding: '8px 12px',
                                    }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        display: 'inline-block',
                                        color: 'var(--accent)',
                                        fontSize: '10px',
                                        fontFamily: 'var(--body-font)',
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        whiteSpace: 'nowrap',
                                        lineHeight: '1',
                                      }}
                                    >
                                      {DEFAULTS.watchOnDemandBadgeText}
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="48px"
                      fontWeight={600}
                      lineHeight="53px"
                      padding="0"
                      margin="20px 56px 56px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ background: 'var(--container)' }}>
                    <ImageWidget src={bodyImg} setImage={setMainImage} width={600} height={320} />

                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="#111111"
                      fontFamily="var(--body-font)"
                      fontSize="24px"
                      fontWeight={500}
                      lineHeight="29px"
                      padding="32px 56px 12px 56px"
                    />

                    <ParagraphWidget
                      value={eventDescription}
                      setValue={setDescription}
                      color="#666666"
                      fontFamily="var(--body-font)"
                      fontSize="16px"
                      fontWeight={400}
                      lineHeight="26px"
                      padding="4px 56px 32px 56px"
                    />

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '12px 56px' }}>
                            <table
                              width="100%"
                              cellPadding={0}
                              cellSpacing={0}
                              border={0}
                              role="presentation"
                              style={{ background: secondaryBg, borderRadius: '12px' }}
                            >
                              <tbody>
                                {durationMinutes !== null && (
                                  <tr>
                                    <td style={{ padding: '24px 24px 10px 24px' }}>
                                      <span style={{ color: 'var(--accent)', marginRight: '12px' }}>●</span>
                                      <span
                                        style={{
                                          color: 'var(--accent)',
                                          fontSize: '15px',
                                          fontFamily: 'var(--body-font)',
                                        }}
                                      >
                                        {DEFAULTS.watchTimeLabel} ~{durationMinutes} min
                                      </span>
                                    </td>
                                  </tr>
                                )}
                                <tr>
                                  <td
                                    style={{
                                      padding: durationMinutes !== null ? '10px 24px' : '24px 24px 10px 24px',
                                    }}
                                  >
                                    <span style={{ color: 'var(--accent)', marginRight: '12px' }}>●</span>
                                    <span
                                      style={{
                                        color: 'var(--accent)',
                                        fontSize: '15px',
                                        fontFamily: 'var(--body-font)',
                                      }}
                                    >
                                      Available Until Dec 2026
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '10px 24px 24px 24px' }}>
                                    <span style={{ color: 'var(--accent)', marginRight: '12px' }}>●</span>
                                    <span
                                      style={{
                                        color: 'var(--accent)',
                                        fontSize: '15px',
                                        fontFamily: 'var(--body-font)',
                                      }}
                                    >
                                      {DEFAULTS.availableOnDemandText}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <ButtonWidget
                      value={_cta}
                      setValue={setCta}
                      href={anchorHref}
                      size="large"
                      color="var(--button-text)"
                      buttonColor="var(--button-color)"
                      border="2px"
                      borderColor="var(--accent)"
                      borderRadius={buttonRadius}
                      fontWeight={700}
                      fontFamily="var(--body-font)"
                      fontSize="16px"
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ background: 'var(--accent-soft)', padding: '48px 64px' }}>
                    <HeaderWidget
                      value={DEFAULTS.taglineText}
                      color="var(--accent)"
                      fontFamily="var(--heading-font)"
                      fontSize="18px"
                      fontWeight={700}
                      padding="0 0 20px 0"
                    />
                    <ParagraphWidget
                      value={DEFAULTS.communityText}
                      color="#8DA08E"
                      fontFamily="var(--body-font)"
                      fontSize="11px"
                      fontWeight={400}
                      lineHeight="17px"
                      padding="0"
                    />
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
